import { ComponentProps, ReactNode } from 'react';
import { withStyles, Title2 as VTitle, CircleButton } from 'vopay';
import styles from './Dialog.module.scss';
import { Dialog as RDialog, VisuallyHidden } from 'radix-ui';

const cn = withStyles(styles);

type DialogRootProps = {
  isOpen: boolean;
  children: ReactNode;
  onClose: () => void;
};

export default function Dialog({ isOpen, children, onClose }: DialogRootProps) {
  return (
    <RDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {children}
    </RDialog.Root>
  );
}

type DialogContentProps = {
  children: ReactNode;
  closeButton?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  // Strips the content's own card chrome (background, padding, radius, shadow, fixed width), leaving a
  // centering-only wrapper like the host portals' placeholder useDialog — the caller supplies its own
  // surface. Centering + z-index are kept, so a position:fixed child (e.g. the eLinx iframe overlay)
  // is still contained and centered.
  bare?: boolean;
  // Only the bottom dialog of a stack paints the overlay, so the backdrop dims once however many dialogs
  // are open — each overlay is 50% black plus a blur, which compounds if every entry renders one.
  // DialogProvider sets this; declarative callers get their overlay by default.
  overlay?: boolean;
  // Position in a stack, 0 being the dialog on top. Each dialog below the top is rendered smaller and
  // darker, so the stack reads as depth. DialogProvider sets this; a lone declarative dialog is always
  // the top.
  depthFromTop?: number;
  // Fires once Radix has unmounted the content, i.e. after the exit animation has played. DialogProvider
  // uses it to drop a settled dialog from its stack and to put focus back where it was. Radix has no
  // such callback, so this rides on onCloseAutoFocus, which FocusScope dispatches from that unmount.
  // Passing it also takes over the close-focus behaviour — see the handler below.
  onExited?: () => void;
};

function Content({
  children,
  closeButton = false,
  closeOnOutsideClick = false,
  closeOnEscape = false,
  bare = false,
  overlay = true,
  depthFromTop = 0,
  onExited,
}: DialogContentProps) {
  return (
    <>
      {overlay && <RDialog.Overlay className={cn('overlay')} data-testid="dialog-overlay" />}

      <RDialog.Content
        onInteractOutside={(e) => {
          if (!closeOnOutsideClick) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!closeOnEscape) {
            e.preventDefault();
          }
        }}
        onCloseAutoFocus={(event) => {
          if (!onExited) return;

          // Radix's own handler always preventDefault()s and focuses context.triggerRef, which only
          // Dialog.Trigger ever sets — so a dialog opened imperatively loses focus to <body>.
          // composeEventHandlers runs ours first and skips Radix's once we preventDefault(), so the
          // owner of onExited (DialogProvider) gets to restore focus itself. A declarative dialog with
          // a Dialog.Trigger passes no onExited and keeps Radix's behaviour untouched.
          event.preventDefault();
          onExited();
        }}
        className={cn('content', bare && 'bare')}
        // Just the depth — Dialog.module.scss turns it into the step-back scale and dim, the way
        // SideDrawer derives its stacked-drawer offsets from an index.
        style={{ '--VP__Dialog__stackDepth': depthFromTop }}
        // No Description concept in this dialog; without this Radix warns about the missing one.
        aria-describedby={undefined}
      >
        {closeButton && (
          <RDialog.Close asChild>
            <CircleButton className={cn('closeButton')} icon="close" circleFill={false} />
          </RDialog.Close>
        )}

        {children}
      </RDialog.Content>
    </>
  );
}

Dialog.Content = Content;

Dialog.Trigger = RDialog.Trigger;

type TitleProps = ComponentProps<typeof VTitle> & {
  hidden?: boolean;
};

// The dialog's accessible name. Every component opened through useDialog() must render one — where it
// wants it, or `hidden` (VisuallyHidden) when the design has no visible heading. Radix wires it to
// aria-labelledby and logs an error on open if it's missing.
function Title({ hidden, ...props }: TitleProps) {
  if (hidden) {
    return (
      <VisuallyHidden.Root>
        <RDialog.Title asChild>
          <VTitle {...props} />
        </RDialog.Title>
      </VisuallyHidden.Root>
    );
  }

  return (
    <RDialog.Title asChild>
      <VTitle level="2" {...props} />
    </RDialog.Title>
  );
}

Dialog.Title = Title;
